import { useCallback, useEffect, useRef } from 'react';
import { TreatmentPlanTemplateControllerApi } from '../api/generated';
import type {
  CreateTreatmentPlanTemplateRequest,
  TreatmentPlanTemplateResponse,
  UpdateTreatmentPlanTemplateRequest
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  removeTemplate,
  selectTreatmentPlanTemplates,
  selectTreatmentPlanTemplatesError,
  selectTreatmentPlanTemplatesLoading,
  setError,
  setLoading,
  setTemplates,
  upsertTemplate
} from '../store/treatmentPlanTemplates/treatmentPlanTemplatesSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseTreatmentPlanTemplatesResult {
  templates: TreatmentPlanTemplateResponse[];
  isLoading: boolean;
  loadError: string | null;
  refresh: () => Promise<void>;
  createTemplate: (
    payload: CreateTreatmentPlanTemplateRequest
  ) => Promise<TreatmentPlanTemplateResponse>;
  updateTemplate: (
    id: string,
    payload: UpdateTreatmentPlanTemplateRequest
  ) => Promise<TreatmentPlanTemplateResponse>;
  deleteTemplate: (id: string) => Promise<void>;
}

export function useTreatmentPlanTemplates(): UseTreatmentPlanTemplatesResult {
  const dispatch = useAppDispatch();
  const templates = useAppSelector(selectTreatmentPlanTemplates);
  const isLoading = useAppSelector(selectTreatmentPlanTemplatesLoading);
  const loadError = useAppSelector(selectTreatmentPlanTemplatesError);
  const hasFetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const api = new TreatmentPlanTemplateControllerApi(createApiConfiguration());
      const response = await api.getTreatmentPlanTemplates();
      dispatch(setTemplates(response.data));
    } catch (e) {
      dispatch(setError(getErrorMessage(e, 'Failed to load treatment plan templates')));
      throw e;
    }
  }, [dispatch]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    refresh().catch(() => {
      // error already surfaced via slice state
    });
  }, [refresh]);

  const createTemplate = useCallback(
    async (payload: CreateTreatmentPlanTemplateRequest) => {
      const api = new TreatmentPlanTemplateControllerApi(createApiConfiguration());
      const response = await api.createTreatmentPlanTemplate({
        createTreatmentPlanTemplateRequest: payload
      });
      dispatch(upsertTemplate(response.data));
      return response.data;
    },
    [dispatch]
  );

  const updateTemplate = useCallback(
    async (id: string, payload: UpdateTreatmentPlanTemplateRequest) => {
      const api = new TreatmentPlanTemplateControllerApi(createApiConfiguration());
      const response = await api.updateTreatmentPlanTemplate({
        treatmentPlanTemplateId: id,
        updateTreatmentPlanTemplateRequest: payload
      });
      dispatch(upsertTemplate(response.data));
      return response.data;
    },
    [dispatch]
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      const api = new TreatmentPlanTemplateControllerApi(createApiConfiguration());
      await api.deleteTreatmentPlanTemplate({ treatmentPlanTemplateId: id });
      dispatch(removeTemplate(id));
    },
    [dispatch]
  );

  return {
    templates,
    isLoading,
    loadError,
    refresh,
    createTemplate,
    updateTemplate,
    deleteTemplate
  };
}
